import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $patchStyleText } from '@lexical/selection'
import { $getRoot, $getSelection, $isRangeSelection, type LexicalEditor } from 'lexical'
import React from 'react'

import { FONT_SIZE } from '../constants'
import type { ToolbarStateProps } from '../text-editor.types'

function clampFontSize(size: number) {
  return Math.min(FONT_SIZE.max, Math.max(FONT_SIZE.min, size))
}

// Inline font size applies to the selected text only (unlike headings, which are
// block-level), via an inline style on the selected text nodes. When the editor
// was never focused there is no selection, so use a collapsed caret at the end:
// $patchStyleText then sets the size for the next typed text without touching the
// existing content.
function applyFontSize(editor: LexicalEditor, size: number) {
  editor.update(() => {
    const selection = $getSelection()
    const target = $isRangeSelection(selection) ? selection : $getRoot().selectEnd()

    $patchStyleText(target, { 'font-size': `${size}px` })
  })
}

type TProps = NonNullable<Pick<ToolbarStateProps, 'fontSize'>>

export const FontSizeStepper = ({ fontSize }: TProps) => {
  const [editor] = useLexicalComposerContext()
  const [value, setValue] = React.useState(String(fontSize))

  React.useEffect(() => {
    setValue(String(fontSize))
  }, [fontSize])

  const current = parseInt(value, 10) || fontSize

  const changeFontSize = (step = 0) => {
    const size = clampFontSize(current + step)
    setValue(String(size))
    applyFontSize(editor, size)
  }

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        className="btn btn-sm btn-ghost px-2!"
        disabled={current <= FONT_SIZE.min}
        onClick={() => changeFontSize(-FONT_SIZE.step)}
        aria-label="Decrease font size"
      >
        −
      </button>
      <input
        type="text"
        inputMode="numeric"
        className="form__field form__field-sm w-14 text-center"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => changeFontSize()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            changeFontSize()
          }
        }}
        aria-label="Font size"
      />
      <button
        type="button"
        className="btn btn-sm btn-ghost px-2!"
        disabled={current >= FONT_SIZE.max}
        onClick={() => changeFontSize(FONT_SIZE.step)}
        aria-label="Increase font size"
      >
        +
      </button>
    </div>
  )
}
